import { BaseRow } from '@sbu/components.datagrid';

import {
  DataGridPageAction,
  DataGridPageState,
  isDiscardPromptingAction,
} from './types';

// eslint-disable-next-line complexity
export const defaultDataGridPageReducer = <
  Row extends BaseRow,
  State extends DataGridPageState<Row>,
  Action extends DataGridPageAction<Row>,
>(
  state: State,
  action: Action
): State => {
  if (isDiscardPromptingAction(action) && state.shouldPromptDiscard) {
    // If we should prompt for discard, handle that first instead of processing immediate action
    return {
      ...state,
      isDiscardDialogOpen: true,
      nextAction: action,
      nextLocation:
        action.type === 'NAVIGATE_AWAY' ? action.nextLocation : undefined,
    };
  }

  // If we're confirming a discard, we should continue using the action that prompted the discard
  const actionToProcess =
    state.nextAction && action.type === 'CONFIRM_DISCARD'
      ? state.nextAction
      : action;

  const sharedNextState = isDiscardPromptingAction(actionToProcess)
    ? {
        ...state,
        isAsideOpen: false,
        isDiscardDialogOpen: false,
        shouldPromptDiscard: false,
        nextAction: undefined,
        nextLocation: undefined,
      }
    : state;

  switch (actionToProcess.type) {
    case 'ADD':
      return {
        ...sharedNextState,
        selectedRow: undefined,
        asideState: 'create',
        isAsideOpen: true,
      };
    case 'EDIT':
      return {
        ...sharedNextState,
        selectedRow: actionToProcess.row,
        asideState: 'update',
        isAsideOpen: true,
      };
    case 'SELECT':
      return {
        ...sharedNextState,
        selectedRow: actionToProcess.row,
        selectedRows: undefined,
        asideState: 'read',
        isAsideOpen: true,
      };
    case 'SELECT_MULTIPLE':
      return {
        ...sharedNextState,
        selectedRow: undefined,
        selectedRows: actionToProcess.rows,
        asideState: actionToProcess.rows ? 'update' : 'read',
        isAsideOpen: !!actionToProcess.rows,
      };
    case 'EXPORT':
      return {
        ...sharedNextState,
        selectedRow: actionToProcess.row,
        asideState: 'read',
      };
    case 'DELETE':
      return {
        ...sharedNextState,
        rowToDelete: actionToProcess.row,
        isDeleteDialogOpen: true,
      };
    case 'CONFIRM_DELETE':
      const isDeletingSelectedRow =
        state.selectedRow?.id === state.rowToDelete?.id;
      return {
        ...sharedNextState,
        selectedRow: isDeletingSelectedRow ? undefined : state.selectedRow,
        rowToDelete: undefined,
        asideState: isDeletingSelectedRow ? 'read' : state.asideState,
        isAsideOpen: state.isAsideOpen && !isDeletingSelectedRow,
        isDeleteDialogOpen: false,
      };
    case 'CLOSE_ASIDE':
      return {
        ...sharedNextState,
        selectedRow: undefined,
        selectedRows: undefined,
        asideState: 'read',
        isAsideOpen: false,
      };
    case 'SAVE':
      return {
        ...sharedNextState,
        shouldPromptDiscard: false,
        selectedRow: actionToProcess.row,
        asideState: 'read',
      };
    case 'CANCEL_DELETE':
      return {
        ...sharedNextState,
        rowToDelete: undefined,
        isDeleteDialogOpen: false,
      };
    case 'CANCEL_DISCARD': {
      return {
        ...sharedNextState,
        isDiscardDialogOpen: false,
        nextAction: undefined,
        nextLocation: undefined,
      };
    }
    case 'UPDATE_ASIDE_FORM': {
      return {
        ...sharedNextState,
        shouldPromptDiscard: actionToProcess.dirty,
      };
    }
    case 'REDIRECT': {
      return {
        ...sharedNextState,
        allowRedirect: true,
        shouldPromptDiscard: false,
        redirectTo: actionToProcess.redirectTo,
      };
    }
    default:
      return sharedNextState;
  }
};
